import type { LocalDatabase } from '../core/types';
import type { RecordedSessionRecord } from '~/powersync/generated/schema';
import type { CreateRecordedSessionInput } from './types';

export const RecordedSessionMutations = {
  /**
   * Attach a recorded session to a workout, idempotent on the primary key. The
   * caller supplies a deterministic `id` (`uuidv5(source|externalId)`), so a
   * re-import of the same session targets the same row instead of a fresh random
   * one — mirroring the server's `ON CONFLICT(id)` upsert and closing the #58
   * `unique_violation` wedge.
   *
   * A pre-existing row is **revived**: `deletedAt` is cleared and the mutable
   * fields (incl. `workoutId`) are refreshed. This matters because `attach()`
   * calls `softDeleteByWorkout` first — without the revive, re-importing the same
   * session would leave its row soft-deleted forever. (BodyMeasurement no-ops on
   * conflict because it has no soft-delete-then-recreate step; RecordedSession
   * does, so it must revive.)
   *
   * This is a read-then-write rather than an `INSERT … ON CONFLICT`, because
   * PowerSync exposes every synced table as a SQLite **view** (backed by
   * `ps_data__*` + INSTEAD-OF triggers) and SQLite rejects UPSERT on a view
   * (`cannot UPSERT a view`). A plain `SELECT` + plain `INSERT`/`UPDATE` are all
   * trigger-supported; wrapping them in one write transaction keeps the check and
   * the write atomic, so two overlapping imports of the same id can't both insert.
   */
  create: async (
    db: LocalDatabase,
    input: CreateRecordedSessionInput,
  ): Promise<RecordedSessionRecord> => {
    const now = new Date().toISOString();

    const record: RecordedSessionRecord = {
      ...input,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    };

    return db.transaction().execute(async (trx) => {
      const existing = await trx
        .selectFrom('RecordedSession')
        .select('id')
        .where('id', '=', record.id)
        .executeTakeFirst();

      if (existing) {
        // Revive + refresh the existing row (it may be soft-deleted from the
        // softDeleteByWorkout that precedes this create).
        await trx
          .updateTable('RecordedSession')
          .set({
            source: record.source,
            externalId: record.externalId,
            startedAt: record.startedAt,
            endedAt: record.endedAt,
            avgHeartRate: record.avgHeartRate,
            maxHeartRate: record.maxHeartRate,
            activeCalories: record.activeCalories,
            recordedDuration: record.recordedDuration,
            workoutId: record.workoutId,
            userId: record.userId,
            deletedAt: null,
            updatedAt: now,
          })
          .where('id', '=', record.id)
          .execute();

        return record;
      }

      await trx.insertInto('RecordedSession').values(record).execute();
      return record;
    });
  },

  /**
   * Detach every session from a workout (soft delete). Used before attaching a
   * fresh match so a re-run never leaves two sessions on one workout.
   */
  softDeleteByWorkout: async (
    db: LocalDatabase,
    workoutId: string,
  ): Promise<void> => {
    const now = new Date().toISOString();
    await db
      .updateTable('RecordedSession')
      .set({ deletedAt: now, updatedAt: now })
      .where('workoutId', '=', workoutId)
      .where('deletedAt', 'is', null)
      .execute();
  },
};
